import { cloneDeep } from 'lodash';
import { baseApi } from 'core/api';
import { useToastStore } from 'store/modules';

const eventDescription = {
    'message.sent': 'Occurs when a message is sent.',
    'message.received': 'Occurs when a message is received.',
    'message.async.created': 'Occurs when a async message is created.',
    'message.async.error': 'Occurs when failed to send async message.',

    'inbox.created': 'Occurs when an inbox is created.',
    'inbox.updated': 'Occurs when an inbox is updated.',
    'inbox.deleted': 'Occurs when an inbox is deleted.',

    'contact.created': 'Occurs when a contact is created.',
    'contact.unsubscribed': 'Occurs when a contact has been unsubscribed.',
    'contact.updated': 'Occurs when a contact is updated.',
    'contact.tag_applied': 'Occurs when a contact has a tag applied.',
    'contact.tag_removed': 'Occurs when a contact has a tag removed.',
    'contact.deleted': 'Occurs when a contact is deleted.',
    'contact.click_link': 'Occurs when a contact clicked a link.',
    'contact.integration_linked': 'Occurs when a contact is linked.',
    'contact.integration_unlinked': 'Occurs when a contact is unlinked.',

    'number.created': 'Occurs when a number is created.',
    'number.updated': 'Occurs when a number is updated.',
    'number.deleted': 'Occurs when a number is deleted.',

    'member.created': 'Occurs when a member is created.',
    'member.updated': 'Occurs when a member is updated.',
    'member.deleted': 'Occurs when a member is deleted.',

    'conversation.opened': 'Occurs when a conversation is opened.',
    'conversation.assigned': 'Occurs when a conversation is assigned.',
    'conversation.closed': 'Occurs when a conversation is closed.',

    'call.inbound_started': 'Occurs whenever a inbound call is started.',
    'call.inbound_ended': 'Occurs whenever a inbound call is ended.',
    'call.outbound_started': 'Occurs when a outbound call started.',
    'call.outbound_ended': 'Occurs when a outbound call ended.',
    'call.recording_available': 'Occurs when a call recording is available.',
    'call.voicemail_available': 'Occurs when a voicemail is available.',
    'call.forwarded': 'Occurs whenever a call is forwarded.',
    'call.outbound_created': 'Occurs whenever an outbound call is made.',
    'call.outbound_answered': 'Occurs whenever an outbound call is answered.',
    'call.outbound_unanswered':
        'Occurs whenever an outbound call is unanswered.',
    'call.inbound_answered': 'Occurs whenever a call is answered.',
    'call.inbound_missed': 'Occurs whenever an inbound call is missed.'
};

const eventTriggers = {
    message: {
        title: 'Messages',
        events: []
    },
    inbox: {
        title: 'Inboxes',
        events: []
    },
    contact: {
        title: 'Contacts',
        events: []
    },
    number: {
        title: 'Numbers',
        events: []
    },
    member: {
        title: 'Members',
        events: []
    },
    conversation: {
        title: 'Conversations',
        events: []
    },
    call: {
        title: 'Calls',
        events: []
    }
};

const defaultPaginationParameters = {
    page: 1,
    sortBy: 'created_at',
    sortOrder: 'desc',
    search: null,
    length: 10
};

const state = () => ({
    _webhooksList: [],
    _webhooksListLoading: true,

    _webhooksPagination: null,
    _webhooksParameters: defaultPaginationParameters,

    _updateWebhookLoading: false,
    _createWebhookLoading: false,

    _detailedWebhook: null,
    _detailedWebhookLoading: false,

    _eventsPreview: null,
    _eventsPreviewLoading: false,

    _eventsList: [],
    _eventsListLoading: true,

    _updateIsActiveWebhookLoading: true,

    _historyList: [],
    _historyListLoading: true,

    _historyPagination: null,
    _historyParameters: defaultPaginationParameters,

    _historyDetails: null,
    _historyDetailsLoading: true
});

const getters = {
    webhooksList: state => state._webhooksList,
    webhooksListLoading: state => state._webhooksListLoading,
    webhooksListPagination: state => state._webhooksPagination,

    detailedWebhook: state => state._detailedWebhook,
    detailedWebhookLoading: state => state._detailedWebhookLoading,

    eventsList: state => state._eventsList,
    eventsListLoading: state => state._eventsListLoading,

    updateWebhookLoading: state => state._updateWebhookLoading,
    updateIsActiveWebhookLoading: state => state._updateIsActiveWebhookLoading,
    createWebhookLoading: state => state._createWebhookLoading,

    historyList: state => state._historyList,
    historyListLoading: state => state._historyListLoading,
    historyListPagination: state => state._historyPagination,

    historyDetails: state => state._historyDetails,
    historyDetailsLoading: state => state._historyDetailsLoading,

    eventsPreview: state => state._eventsPreview,
    eventsPreviewLoading: state => state._eventsPreviewLoading,

    eventsListFormatted: state => {
        const list = cloneDeep(eventTriggers);

        state._eventsList.forEach(item => {
            const title = item.split('.')[0];

            list[title] = {
                ...list[title],
                events: list[title].events.concat({
                    field: item,
                    title: item,
                    description: eventDescription[item] || ''
                })
            };
        });

        return list;
    }
};

const actions = {
    webhooksUpdatePage(page) {
        this._setWebhooksUpdatePage(page);
        this.fetchWebhooksList();
    },

    webhooksHistoryUpdatePage({ page, webhookId }) {
        this._setWebhooksHistoryUpdatePage(page);
        this.fetchHistoryList({ webhookId });
    },

    clenEventsPreview() {
        this._setCleanWebhookEventsPreview();
    },

    fetchWebhooksList() {
        this._setWebhooksListLoading(true);

        return baseApi
            .get('external-webhooks/api/external-webhooks/list', {
                params: this._webhooksParameters
            })
            .then(response => {
                this._setWebhooksListLoaded(response.data);
                return response.data;
            })
            .catch(err => {
                useToastStore().showToast({
                    type: 'danger',
                    message: err.response?.data?.message || err.message
                });
            })
            .finally(() => {
                this._setWebhooksListLoading(false);
            });
    },

    fetchDetailedWebhook({ webhookId }) {
        this._setDetailedWebhookLoading(true);

        return baseApi
            .get(`external-webhooks/api/external-webhooks/${webhookId}/view`)
            .then(response => {
                this._setDetailedWebhookLoaded(response.data);
                return response.data;
            })
            .catch(err => {
                useToastStore().showToast({
                    type: 'danger',
                    message: err.response?.data?.message || err.message
                });
            })
            .finally(() => {
                this._setDetailedWebhookLoading(false);
            });
    },

    fetchEventsList() {
        this._setWebhookEventsListLoading(true);

        return baseApi
            .get('external-webhooks/api/events/list')
            .then(response => {
                this._setWebhookEventsListLoaded(response.data);
                return response.data;
            })
            .catch(err => {
                useToastStore().showToast({
                    type: 'danger',
                    message: err.response?.data?.message || err.message
                });
            })
            .finally(() => {
                this._setWebhookEventsListLoading(false);
            });
    },

    createWebhook(form) {
        this._setCreateWebhookLoading(true);

        return baseApi
            .post(
                'external-webhooks/api/external-webhooks/create',
                JSON.stringify({
                    name: form.name,
                    url: form.url,
                    events: form.events,
                    throttle_limit: form.throttle_limit,
                }),
                {
                    headers: {
                        'Content-type': 'application/json'
                    }
                }
            )
            .then(() => {
                useToastStore().showToast({
                    type: 'success',
                    message: 'Webhook was created successfully'
                });
                this._setCreateWebhookLoading(false);
            })
            .catch(err => {
                useToastStore().showToast({
                    type: 'danger',
                    message: err.response?.data?.message || err.message
                });
                this._setCreateWebhookLoading(false);
                throw err;
            });
    },

    updateWebhook({ form, webhookId }) {
        this._setUpdateWebhookLoading(true);

        return baseApi
            .put(
                `external-webhooks/api/external-webhooks/${webhookId}/update`,
                {
                    name: form.name,
                    url: form.url,
                    events: form.events,
                    throttle_limit: form.throttle_limit,
                },
            )
            .then(() => {
                useToastStore().showToast({
                    type: 'success',
                    message: 'Webhook was updated successfully'
                });
            })
            .catch(e => {
                useToastStore().showToast({
                    type: 'danger',
                    message: e.response?.data?.message || e.message
                });
            })
            .finally(() => {
                this._setUpdateWebhookLoading(false);
            });
    },

    updateIsActive({ webhookId, webhook }) {
        this._setUpdateIsActiveWebhookLoading(true);

        return baseApi
            .put(
                `external-webhooks/api/external-webhooks/${webhookId}/update`,
                {
                    name: webhook.name,
                    url: webhook.url,
                    events: webhook.events,
                    is_active: !webhook.is_active
                },
            )
            .then(response => {
                this._setUpdateIsActiveWebhookLoaded(response.data);
                useToastStore().showToast({
                    type: 'success',
                    message: 'Webhook was updated successfully'
                });
                this._setUpdateIsActiveWebhookLoading(false);
            })
            .catch(err => {
                useToastStore().showToast({
                    type: 'danger',
                    message: err.response?.data?.message || err.message
                });
                this._setUpdateIsActiveWebhookLoading(false);
                throw err;
            });
    },

    deleteWebhook({ webhookId }) {
        return baseApi
            .delete(`external-webhooks/api/external-webhooks/${webhookId}/delete`)
            .then(response => {
                this._setDeleteWebhookLoaded(webhookId);
                useToastStore().showToast({
                    type: 'success',
                    message: 'Webhook was deleted successfully'
                });
                return response;
            })
            .catch(err => {
                useToastStore().showToast({
                    type: 'danger',
                    message: err.message
                });
            });
    },

    fetchHistoryList({ webhookId, limit = 10, page = 1 }) {
        this._setWebhookHistoryListLoading(true);

        return baseApi
            .get(`external-webhooks/api/external-webhooks/${webhookId}/requests-history`, {
                params: this._historyParameters
            })
            .then(response => {
                this._setWebhookHistoryListLoaded(response.data);
                return response.data.data;
            })
            .catch(err => {
                useToastStore().showToast({
                    type: 'danger',
                    message: err.message
                });
            })
            .finally(() => {
                this._setWebhookHistoryListLoading(false);
            });
    },

    fetchHistoryDetails({ historyId }) {
        this._setWebhookHistoryDetailsLoading(true);

        return baseApi
            .get(`external-webhooks/api/requests-history/${historyId}/view`)
            .then(response => {
                this._setWebhookHistoryDetailsLoaded(response.data);
                return response.data;
            })
            .catch(err => {
                useToastStore().showToast({
                    type: 'danger',
                    message: err.response?.data?.message || err.message
                });
            })
            .finally(() => {
                this._setWebhookHistoryDetailsLoading(false);
            });
    },

    fetchEventsPreview({ eventsList }) {
        if (eventsList.length === 0) {
            return this._setWebhookToggleTriggerLoaded(null);
        }

        this._setWebhookToggleTriggerLoading(true);

        return baseApi
            .get('external-webhooks/api/events/preview', { params: { events: eventsList } })
            .then(response => {
                this._setWebhookToggleTriggerLoaded(response.data);
                return response.data;
            })
            .catch(err => {
                useToastStore().showToast({
                    type: 'danger',
                    message: err.response?.data?.message || err.message
                });
            })
            .finally(() => {
                this._setWebhookToggleTriggerLoading(false);
            });
    },

    // Private methods for all operations
    _setWebhooksListLoading(status = false) {
        this._webhooksListLoading = status;
    },

    _setWebhooksListLoaded(data) {
        this._webhooksList = data.data;
        this._webhooksPagination = {
            current_page: data.meta.current_page,
            from: data.meta.from,
            last_page: data.meta.last_page,
            per_page: data.meta.per_page,
            to: data.meta.to,
            total: data.meta.total
        };
    },

    _setWebhooksUpdatePage(data) {
        this._webhooksParameters.page = data;
    },

    _setDetailedWebhookLoading(status = false) {
        this._detailedWebhookLoading = status;
    },

    _setDetailedWebhookLoaded(data) {
        this._detailedWebhook = data;
    },

    _setWebhookEventsListLoading(status = false) {
        this._eventsListLoading = status;
    },

    _setWebhookEventsListLoaded(data) {
        this._eventsList = data;
    },

    _setCreateWebhookLoading(status = false) {
        this._createWebhookLoading = status;
    },

    _setUpdateWebhookLoading(status = false) {
        this._updateWebhookLoading = status;
    },

    _setUpdateIsActiveWebhookLoading(status = false) {
        this._updateIsActiveWebhookLoading = status;
    },

    _setDeleteWebhookLoaded(webhookId) {
        this._webhooksList = this._webhooksList.filter(
            item => item.id !== webhookId
        );
        
        if (this._webhooksPagination) {
            --this._webhooksPagination.total;
        }
    },

    _setWebhookHistoryListLoading(status = false) {
        this._historyListLoading = status;
    },

    _setWebhookHistoryListLoaded(data) {
        this._historyList = data.data;
        this._historyPagination = {
            current_page: data.meta.current_page,
            from: data.meta.from,
            last_page: data.meta.last_page,
            per_page: data.meta.per_page,
            to: data.meta.to,
            total: data.meta.total
        };
    },

    _setWebhooksHistoryUpdatePage(data) {
        this._historyParameters.page = data;
    },

    _setWebhookHistoryDetailsLoading(status = false) {
        this._historyDetailsLoading = status;
    },

    _setWebhookHistoryDetailsLoaded(data) {
        this._historyDetails = data;
    },

    _setWebhookToggleTriggerLoading(status = false) {
        this._eventsPreviewLoading = status;
    },

    _setWebhookToggleTriggerLoaded(data) {
        this._eventsPreview = data;
    },

    _setCleanWebhookEventsPreview() {
        this._eventsPreview = null;
    },

    _setUpdateIsActiveWebhookLoaded(data) {
        this._webhooksList = this._webhooksList.map(item =>
            item.id === data.id ? { ...item, is_active: data.is_active } : item
        );
    }
};



export default {
    state,
    getters,
    actions
};
